import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Package, Loader2, HandHeart, Building2, Share2, X, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { SectionHeader } from "@/components/ui-kit";
import ShareBar from "@/components/ShareBar";

const URGENCY = { high: "text-red-400 border-red-400/40", medium: "text-[#D4AF37] border-[#D4AF37]/40", low: "text-[#8A877B] border-white/20" };

export default function InKind() {
  useSeo("In-Kind Wish List", "Donate equipment, supplies and skilled labor to Golden Wolf Foundation. Browse our live wish list of items still needed.");
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [mode, setMode] = useState("donate");

  const load = () => api.get("/inkind/items").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <SectionHeader overline="In-Kind Marketplace" title="The Wish List"
        sub="Every item here builds the Golden Wolf Campus and equips our dogs and veterans. Claim what you can give — nothing is marked received until our team verifies delivery." />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((it, i) => {
          const pct = it.qty_needed ? Math.min(100, Math.round((it.qty_received / it.qty_needed) * 100)) : 0;
          return (
            <motion.div key={it.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}
              className="gw-card p-6 flex flex-col" data-testid={`item-${it.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 flex items-center justify-center bg-[#1C1C1C] border border-white/10"><Package className="w-5 h-5 text-[#D4AF37]" /></div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${URGENCY[it.urgency] || URGENCY.low}`}>{it.urgency} priority</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#FDFBF7] mt-4">{it.name}</h3>
              <p className="text-sm text-[#8A877B] mt-1 flex-1">{it.description}</p>

              <div className="mt-4 grid grid-cols-2 gap-y-1 text-xs text-[#C8C4B7]">
                <span>Needed: <b className="text-[#FDFBF7]">{it.qty_needed}</b></span>
                <span>Pledged: <b className="text-[#FDFBF7]">{it.qty_pledged_active}</b></span>
                <span>Condition: <b className="text-[#FDFBF7]">{it.condition}</b></span>
                <span>{it.estimated_value ? `Est. $${it.estimated_value}` : "Labor"}</span>
              </div>
              <div className="mt-3">
                <div className="h-1.5 bg-[#1C1C1C] overflow-hidden"><div className="h-full bg-[#D4AF37]" style={{ width: `${pct}%` }} /></div>
                <div className="flex justify-between text-[11px] text-[#8A877B] mt-1"><span>{it.qty_received} verified</span><span>{it.qty_outstanding} still needed · by {it.deadline}</span></div>
              </div>

              <div className="mt-5 flex gap-2">
                <button onClick={() => { setActive(it); setMode("donate"); }} data-testid={`donate-this-${it.id}`} className="btn-gold text-sm flex-1 flex items-center justify-center gap-1"><HandHeart className="w-4 h-4" /> I Can Donate</button>
                <button onClick={() => { setActive(it); setMode("sponsor"); }} data-testid={`sponsor-this-${it.id}`} className="btn-outline text-sm flex items-center justify-center gap-1 px-3"><Building2 className="w-4 h-4" /></button>
                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/wishlist`); toast.success("Share link copied"); }} data-testid={`share-this-${it.id}`} className="border border-white/20 text-[#C8C4B7] hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors px-3 flex items-center"><Share2 className="w-4 h-4" /></button>
              </div>
            </motion.div>
          );
        })}
      </div>

      {active && <PledgeModal item={active} mode={mode} onClose={() => setActive(null)} onDone={() => { setActive(null); load(); }} />}
    </div>
  );
}

function PledgeModal({ item, mode, onClose, onDone }) {
  const [f, setF] = useState({
    donor_name: "", organization: "", email: "", phone: "", brand_model: "", condition: "New",
    quantity: 1, approximate_value: item.estimated_value || "", pickup_required: "No",
    delivery_availability: "", available_datetime: "", message: "",
    public_recognition: false, tax_acknowledgment: true, terms: false,
  });
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.terms) return toast.error("Please confirm the donation terms");
    setLoading(true);
    try {
      let photo_url = null;
      if (photo) {
        const fd = new FormData(); fd.append("file", photo);
        const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photo_url = up.data.path;
      }
      const endpoint = mode === "sponsor" ? "/sponsors/inquiry" : "/inkind/pledge";
      const payload = mode === "sponsor"
        ? { organization: f.organization || f.donor_name, contact_name: f.donor_name, email: f.email, phone: f.phone, tier: "Custom In-Kind Partner", item: item.name, message: f.message }
        : { item_id: item.id, ...f, photo_url };
      await api.post(endpoint, payload);
      toast.success(mode === "sponsor" ? "Thank you — our team will reach out." : "Pledge received! We'll contact you to coordinate.");
      onDone();
    } catch {
      toast.error("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-6" onClick={onClose} data-testid="pledge-modal">
      <div className="bg-[#141414] border border-white/10 w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-[#141414] z-10">
          <div>
            <div className="overline">{mode === "sponsor" ? "Sponsor This Item" : "I Can Donate This"}</div>
            <h3 className="font-heading text-2xl font-semibold text-[#FDFBF7]">{item.name}</h3>
          </div>
          <button onClick={onClose} data-testid="pledge-close" className="text-[#8A877B] hover:text-[#FDFBF7]"><X /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid sm:grid-cols-2 gap-4">
          <In label="Your name" v={f.donor_name} on={(v) => set("donor_name", v)} req testid="pl-name" />
          <In label="Business / organization" v={f.organization} on={(v) => set("organization", v)} testid="pl-org" />
          <In label="Email" type="email" v={f.email} on={(v) => set("email", v)} req testid="pl-email" />
          <In label="Phone" v={f.phone} on={(v) => set("phone", v)} testid="pl-phone" />
          {mode === "donate" && <>
            <In label="Brand & model" v={f.brand_model} on={(v) => set("brand_model", v)} testid="pl-brand" />
            <Sel label="Condition" v={f.condition} on={(v) => set("condition", v)} opts={["New", "Gently used", "Loan"]} testid="pl-cond" />
            <In label="Quantity" type="number" v={f.quantity} on={(v) => set("quantity", v)} testid="pl-qty" />
            <In label="Approx. value ($)" type="number" v={f.approximate_value} on={(v) => set("approximate_value", v)} testid="pl-value" />
            <Sel label="Pickup required?" v={f.pickup_required} on={(v) => set("pickup_required", v)} opts={["No", "Yes"]} testid="pl-pickup" />
            <In label="Delivery availability" v={f.delivery_availability} on={(v) => set("delivery_availability", v)} testid="pl-delivery" />
            <In label="Available date & time" type="datetime-local" v={f.available_datetime} on={(v) => set("available_datetime", v)} testid="pl-datetime" />
            <div className="sm:col-span-2">
              <label className="block text-sm text-[#C8C4B7] mb-2">Photo of item</label>
              <label className="gw-input flex items-center gap-2 cursor-pointer" data-testid="pl-photo">
                <Upload className="w-4 h-4 text-[#D4AF37]" /> <span className="text-[#8A877B] text-sm">{photo ? photo.name : "Upload a photo (optional)"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files[0])} />
              </label>
            </div>
          </>}
          <div className="sm:col-span-2">
            <label className="block text-sm text-[#C8C4B7] mb-2">Message</label>
            <textarea rows={3} className="gw-input" data-testid="pl-message" value={f.message} onChange={(e) => set("message", e.target.value)} />
          </div>
          <div className="sm:col-span-2 space-y-2.5 pt-2">
            <Chk label="I give permission for public recognition" v={f.public_recognition} on={() => set("public_recognition", !f.public_recognition)} testid="pl-recognition" />
            <Chk label="I would like a tax acknowledgment" v={f.tax_acknowledgment} on={() => set("tax_acknowledgment", !f.tax_acknowledgment)} testid="pl-tax" />
            <Chk label="I confirm the donation terms and that this item is offered in good faith" v={f.terms} on={() => set("terms", !f.terms)} testid="pl-terms" />
          </div>
          <button type="submit" disabled={loading} className="btn-gold sm:col-span-2 flex items-center justify-center gap-2 py-3.5" data-testid="pledge-submit">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} {mode === "sponsor" ? "Submit Sponsorship" : "Submit Pledge"}
          </button>
        </form>
      </div>
    </div>
  );
}

const In = ({ label, v, on, type = "text", req, testid }) => (
  <div><label className="block text-sm text-[#C8C4B7] mb-2">{label}{req && <span className="text-[#D4AF37]"> *</span>}</label>
    <input type={type} required={req} value={v} onChange={(e) => on(e.target.value)} className="gw-input" data-testid={testid} /></div>
);
const Sel = ({ label, v, on, opts, testid }) => (
  <div><label className="block text-sm text-[#C8C4B7] mb-2">{label}</label>
    <select value={v} onChange={(e) => on(e.target.value)} className="gw-input" data-testid={testid}>{opts.map((o) => <option key={o}>{o}</option>)}</select></div>
);
const Chk = ({ label, v, on, testid }) => (
  <label className="flex items-start gap-3 cursor-pointer text-sm text-[#C8C4B7]" data-testid={testid} onClick={on}>
    <span className={`w-5 h-5 border shrink-0 flex items-center justify-center mt-0.5 ${v ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/30"}`}>{v && <span className="text-black text-xs">✓</span>}</span>{label}
  </label>
);
